import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { CButton, CCol, CFormFeedback, CFormInput, CFormLabel, CPopover, CRow } from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { DEFAULT_FN, EXTENSION_ALLOW } from '../../../constants';
import { getFileData, toastError } from '../../../utils';
import { callApi } from '../../../apiCaller';
import { Media } from '../Media';
import { CenterSpinner } from '../Loadings';
import { useDispatch } from 'react-redux';
import { setShowRemovePopup } from '../../../actions/general';
import PopupReview from '../Media/PopupReview';

const SimpleInputMediaGroup = ({
     id,
     label,
     isRequired,
     value,
     error,
     setFieldValue,
     disabled,
     onBlur,
     fieldName,
     allow,
     tooltip,
     confirmDelete,
     confirmRemoveFile,
     showIconRemove,
     editInAccordion,
     setShowPopupRemoveFile = DEFAULT_FN,
     setFileRemove,
}) => {
     const [uploading, setUploading] = useState(false);
     const fileNameRef = useRef('');
     const dispatch = useDispatch();

     const fileData = getFileData(value);
     const isImage = ['jpg', 'jpeg', 'png', 'jfif', 'webp', 'gif', 'svg'].includes(fileData.extension);

     if (value && value.match(/\.[0-9a-z]+$/i) !== null) {
          fileNameRef.current = value?.substr(value.lastIndexOf('/') + 1);
     } else {
          fileNameRef.current = '';
     }

     if (showIconRemove === null || showIconRemove === undefined) {
          showIconRemove = true;
     }
     const isInvalid = Boolean(isRequired && error);

     const onFileUpload = (e) => {
          const selectedFile = e.target.files[0];

          if (!selectedFile) {
               return;
          }

          if (!selectedFile.name.toLowerCase().match(allow)) {
               switch (allow) {
                    case EXTENSION_ALLOW.DOC_EXCEL:
                         toast.error('Only pdf, excel and doc files are allowed!');
                         break;
                    case EXTENSION_ALLOW.IMAGE_AND_VIDEO:
                         toast.error('Only images and videos files are allowed!');
                         break;
                    case EXTENSION_ALLOW.IMAGE_ONLY:
                         toast.error('Only images files are allowed!');
                         break;
                    case EXTENSION_ALLOW.MEDIA:
                    default:
                         toast.error('Only pdf, excel and doc files are allowed!');
                         break;
               }

               return;
          }

          const formDataUploadFile = new FormData();
          formDataUploadFile.append('file', selectedFile);

          setUploading(true);

          callApi(`media`, 'POST', formDataUploadFile)
               .then((response) => {
                    setUploading(false);

                    if (response.status !== 200) {
                         toastError(response);
                         return;
                    }

                    setFieldValue(id, response.data.file.location);

                    if (fieldName) {
                         const fileName = selectedFile.name.split('.')[0];
                         setFieldValue(fieldName, fileName);
                    }
               })
               .finally(() => {
                    e.target.value = '';
               });
     };

     const handleTriggerClickFileInput = () => {
          document.getElementById(id).click();
     };

     const handleCLickRemoveFileInput = () => {
          fileNameRef.current = '';
          setFieldValue(id, '');

          if (fieldName) {
               setFieldValue(fieldName, '');
          }
     };

     const handleCLickShowPopupRemoveFile = () => {
          setShowPopupRemoveFile(true);

          if (typeof setFileRemove === 'function') {
               setFileRemove(id);
          }

          if (confirmDelete) {
               dispatch(setShowRemovePopup(true));
          }
     };

     const [visible, setVisible] = useState(false);

     return (
          <div className="form-group simple-input-media">
               <CRow>
                    <CFormLabel htmlFor={id} className="d-flex">
                         {<span className="me-1">{label}</span>}
                         {isRequired && <span className="text-danger">*</span>}
                         {tooltip && (
                              <CPopover content={tooltip} placement="top" trigger="hover">
                                   <CIcon icon="icon-info-cilcle" />
                              </CPopover>
                         )}
                         {uploading && <CenterSpinner size="sm" className="ms-2" />}
                    </CFormLabel>
                    <CCol md={12}>
                         <div className="form-input-wrap">
                              <div
                                   className={`input-file-custom d-flex align-items-stretch w-100${isInvalid ? ' is-invalid' : ''}`}
                                   onClick={handleTriggerClickFileInput}
                              >
                                   <CButton className="choose-file">Choose File</CButton>
                                   <CFormInput value={fileNameRef.current} disabled invalid={isInvalid} />
                              </div>
                              {showIconRemove && (
                                   <>
                                        {fileNameRef.current && !confirmRemoveFile && (
                                             <CButton className="btn-remove" color="danger" onClick={handleCLickRemoveFileInput}>
                                                  <i className="fal fa-times" />
                                             </CButton>
                                        )}
                                        {fileNameRef.current && confirmRemoveFile && (
                                             <CButton className="btn-remove" color="danger" onClick={handleCLickShowPopupRemoveFile}>
                                                  <i className="fal fa-times" />
                                             </CButton>
                                        )}
                                   </>
                              )}
                         </div>
                         <CFormInput
                              id={id}
                              type="file"
                              accept=".jpg,.jpeg,.png,.gif,.pdf,.mp4,.docx,.doc,.xls,.xlsx,.xlsm,.svg"
                              invalid={isInvalid}
                              onChange={onFileUpload}
                              disabled={disabled || uploading}
                              onBlur={onBlur}
                              className="hidden"
                         />
                         {isInvalid && <CFormFeedback invalid>{error}</CFormFeedback>}
                    </CCol>
                    {fileNameRef.current && isImage && (
                         <CCol md={12} className="d-flex align-items-center">
                              <PopupReview
                                   visible={visible}
                                   setVisible={setVisible}
                                   fileName={fileNameRef.current}
                                   isLoading={uploading}
                                   src={value}
                                   editInAccordion={editInAccordion}
                              />
                              <div onClick={() => setVisible(!visible)}>
                                   <Media fileName={fileNameRef.current} isLoading={uploading} src={value} editInAccordion={editInAccordion} />
                              </div>
                         </CCol>
                    )}
               </CRow>
          </div>
     );
};

SimpleInputMediaGroup.propTypes = {
     id: PropTypes.string,
     label: PropTypes.string,
     isRequired: PropTypes.bool,
     value: PropTypes.any,
     error: PropTypes.string,
     setFieldValue: PropTypes.func,
     disabled: PropTypes.bool,
     tooltip: PropTypes.string,
     setFileRemove: PropTypes.func,
};

SimpleInputMediaGroup.defaultProps = {
     value: '',
     allow: EXTENSION_ALLOW.IMAGE_ONLY,
};

export default SimpleInputMediaGroup;
